import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FolderIcon from "@mui/icons-material/Folder";
import { useTranslation } from "react-i18next";

interface TaskSelectionOverlayProps {
  selectedTasks: string[];
  onClearSelection: () => void;
  onStatusChange: (anchorEl: HTMLElement) => void;
  onAssigneesChange: (anchorEl: HTMLElement) => void;
  onDatesChange: (anchorEl: HTMLElement) => void;
  onPriorityChange: (anchorEl: HTMLElement) => void;
  onTagsChange: (anchorEl: HTMLElement) => void;
  onProjectChange: (anchorEl: HTMLElement) => void;
  onCustomFieldsChange?: (anchorEl: HTMLElement) => void;
  onMoveAdd?: () => void;
  onConvertToTask?: () => void;
  onCopy?: () => void;
  onDelete: () => void;
}

/**
 * TaskSelectionOverlay - A component that appears at the bottom of the screen
 * when one or more tasks are selected.
 */
const TaskSelectionOverlay: React.FC<TaskSelectionOverlayProps> = ({
  selectedTasks = [],
  onClearSelection,
  onStatusChange,
  onAssigneesChange,
  onDatesChange,
  onPriorityChange,
  onTagsChange,
  onProjectChange,
  onCustomFieldsChange,
  onMoveAdd,
  onConvertToTask,
  onCopy,
  onDelete,
}) => {
  const { t } = useTranslation(["bugherd"]);

  // Create refs for the buttons
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const assigneesButtonRef = useRef<HTMLButtonElement>(null);
  const datesButtonRef = useRef<HTMLButtonElement>(null);
  const priorityButtonRef = useRef<HTMLButtonElement>(null);
  const tagsButtonRef = useRef<HTMLButtonElement>(null);
  const projectButtonRef = useRef<HTMLButtonElement>(null);
  const customFieldsButtonRef = useRef<HTMLButtonElement>(null);

  // Only show the overlay if there are selected tasks
  if (!selectedTasks.length) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: "40px",
        backgroundColor: "#212121",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1,
        zIndex: 1300,
        boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Left side with selection info and primary actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1,
            py: 0.5,
            borderRadius: "4px",
            mr: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
            {selectedTasks.length}{" "}
            {selectedTasks.length === 1
              ? t("Task selected")
              : t("Tasks selected")}
          </Typography>
          <IconButton
            size="small"
            onClick={onClearSelection}
            sx={{ color: "rgba(255, 255, 255, 0.7)", p: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Tooltip title={t("Change status")}>
          <Button
            ref={statusButtonRef}
            size="small"
            startIcon={<LabelOutlinedIcon />}
            onClick={() =>
              statusButtonRef.current && onStatusChange(statusButtonRef.current)
            }
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              borderRadius: "4px",
              minWidth: "unset",
              px: 1,
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            {t("Status")}
          </Button>
        </Tooltip>

        <Tooltip title={t("Assign to")}>
          <Button
            ref={assigneesButtonRef}
            size="small"
            startIcon={<PersonOutlineIcon />}
            onClick={() =>
              assigneesButtonRef.current &&
              onAssigneesChange(assigneesButtonRef.current)
            }
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              borderRadius: "4px",
              minWidth: "unset",
              px: 1,
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            {t("Assignees")}
          </Button>
        </Tooltip>

        <Tooltip title={t("Set dates")}>
          <Button
            ref={datesButtonRef}
            size="small"
            startIcon={<CalendarTodayIcon />}
            onClick={() =>
              datesButtonRef.current && onDatesChange(datesButtonRef.current)
            }
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              borderRadius: "4px",
              minWidth: "unset",
              px: 1,
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            {t("Dates")}
          </Button>
        </Tooltip>

        <Tooltip title={t("Set priority")}>
          <Button
            ref={priorityButtonRef}
            size="small"
            startIcon={<FlagIcon />}
            onClick={() =>
              priorityButtonRef.current &&
              onPriorityChange(priorityButtonRef.current)
            }
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              borderRadius: "4px",
              minWidth: "unset",
              px: 1,
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            {t("Priority")}
          </Button>
        </Tooltip>

        <Tooltip title={t("Add or remove tags")}>
          <Button
            ref={tagsButtonRef}
            size="small"
            startIcon={<LocalOfferIcon />}
            onClick={() =>
              tagsButtonRef.current && onTagsChange(tagsButtonRef.current)
            }
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              borderRadius: "4px",
              minWidth: "unset",
              px: 1,
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            {t("Tags")}
          </Button>
        </Tooltip>

        <Tooltip title={t("Change project")}>
          <Button
            ref={projectButtonRef}
            size="small"
            startIcon={<FolderIcon />}
            onClick={() =>
              projectButtonRef.current &&
              onProjectChange(projectButtonRef.current)
            }
            sx={{
              color: "white",
              textTransform: "none",
              fontSize: "13px",
              borderRadius: "4px",
              minWidth: "unset",
              px: 1,
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            {t("Project")}
          </Button>
        </Tooltip>

        {/* Uncomment if you want to add custom fields functionality */}
        {/* {onCustomFieldsChange && (
          <Tooltip title={t("Edit custom fields")}>
            <Button
              ref={customFieldsButtonRef}
              size="small"
              startIcon={<ListAltOutlinedIcon />}
              onClick={() => customFieldsButtonRef.current && onCustomFieldsChange(customFieldsButtonRef.current)}
              sx={{
                color: "white",
                textTransform: "none",
                fontSize: "13px",
                borderRadius: "4px",
                minWidth: "unset",
                px: 1,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              {t("Custom Fields")}
            </Button>
          </Tooltip>
        )} */}
      </Box>

      {/* Right side with secondary actions */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Uncomment if you want to add these additional actions */}
        {/* {onMoveAdd && (
          <Tooltip title={t("Move to or add to another location")}>
            <Button
              size="small"
              onClick={onMoveAdd}
              sx={{
                color: "white",
                textTransform: "none",
                fontSize: "13px",
                borderRadius: "4px",
                minWidth: "unset",
                px: 1,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              {t("Move/Add")}
            </Button>
          </Tooltip>
        )}

        {onConvertToTask && (
          <Tooltip title={t("Convert to task")}>
            <Button
              size="small"
              startIcon={<AssignmentIcon />}
              onClick={onConvertToTask}
              sx={{
                color: "white",
                textTransform: "none",
                fontSize: "13px",
                borderRadius: "4px",
                minWidth: "unset",
                px: 1,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              {t("Convert to Task")}
            </Button>
          </Tooltip>
        )}

        {onCopy && (
          <Tooltip title={t("Copy")}>
            <IconButton size="small" onClick={onCopy} sx={{ color: "white" }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 0.5, bgcolor: "rgba(255, 255, 255, 0.2)" }}
        /> */}

        <Tooltip title={t("Delete")}>
          <IconButton size="small" onClick={onDelete} sx={{ color: "white" }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* <Tooltip title={t("More options")}>
          <IconButton size="small" sx={{ color: "white" }}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Tooltip> */}
      </Box>
    </Box>
  );
};

export default TaskSelectionOverlay;
